import type { KeyboardEvent } from "react";

/**
 * Keyboard activation for card-shaped links.
 *
 * Every index grid on the site — organizations, events, professionals,
 * articles, regulations — rendered its cards as clickable `<div>`s with
 * only an `onClick`. That makes those directories **completely
 * unreachable by keyboard**: no tab stop, no Enter/Space activation, and
 * nothing announced to a screen reader (WCAG 2.1.1 Keyboard, 4.1.2 Name
 * Role Value).
 *
 * Spread the result onto the clickable card. It supplies the role, the
 * tab stop, an accessible name, and Enter/Space handling — matching a link (Enter) plus Space — without restructuring every card into an anchor.
 *
 *   <Card {...cardActivationProps(`Open ${org.orgName}`, () => go(org))} />
 *
 * Interactive controls INSIDE a card (a save toggle, a menu button) must
 * still stop propagation on their own handlers, exactly as they already
 * do for the click path.
 */
export function cardActivationProps(
    label: string,
    activate: () => void,
): {
    role: "link";
    tabIndex: 0;
    "aria-label": string;
    onClick: () => void;
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
} {
    return {
        role: "link",
        tabIndex: 0,
        "aria-label": label,
        onClick: activate,
        onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
            // Ignore keys bubbling from a nested control (e.g. Space on an
            // inner button) so the card doesn't navigate on top of it.
            if (event.target !== event.currentTarget) return;
            if (event.key === "Enter" || event.key === " ") {
                // Space scrolls the page by default on a non-button.
                event.preventDefault();
                activate();
            }
        },
    };
}
